import {
  AnalyticsActionCancelEvent,
  AnalyticsActionItemSelectEvent,
  AnalyticsActionSaveCampaignAcceptEvent,
  AnalyticsActionSaveCampaignDeclineEvent,
  AnalyticsEventParam,
  PaywallResult,
  ProductType,
  ScreenType,
} from '@accedo/one-plugin-sdk';
import {
  Action,
  ActionKind,
  ActionsDictionary,
  applyDatabinding,
  Context as DynUiContext,
  eventHandlers,
  Item as DynUiItem,
} from '@dynamic/ui';
import { RefObject } from 'react';
import { useSWRConfig } from 'swr';

import { userService } from '#/client/api';
import {
  FAVORITES_CACHE_KEY,
  WATCH_HISTORY_CACHE_KEY,
} from '#/client/constants';
import {
  Item as ClientItem,
  ItemRefs,
  mediaTypeToResourceType,
  ResourceType,
} from '#/client/models';
import { SharingModalApi } from '#/components/Modal/SharingModal';
import { ShowMoreModalApi } from '#/components/Modal/ShowMoreModal';
import { Errors } from '#/errors/errors';
import { OneError } from '#/errors/oneError';
import { useNavigation } from '#/hooks/useNavigation';
import {
  getScreenTitle,
  getScreenType,
} from '#/plugins/analytics/analyticsUtils';
import { ShowPaywallParams } from '#/plugins/paywall/models';
import { InternalPageId } from '#/service/constants/menu';
import { destinationRoutes, Path } from '#/service/routes';
import { falsyToNull, ignoreError } from '#/service/utils';
import { authActions, authStore } from '#/store/auth/auth.store';
import { favoritesActions } from '#/store/favorites/favorites.store';
import { historyActions } from '#/store/history/history.store';
import { paywallActions, paywallStore } from '#/store/paywall/paywall.store';
import { privacyActions } from '#/store/privacy/privacy.store';
import { snackbarActions } from '#/store/snackbar/snackbar.store';
import { upNextActions } from '#/store/upNext/upNext.store';

import { generatePagePathname } from './pathUtils';

interface ClientContext {
  item?: ClientItem;
  container?: {
    title?: string;
    _meta?: { containerIndex?: number; containerId?: string };
  };
  screenType?: ScreenType;
}

const getItemRefs = (
  itemId: string | undefined,
  resourceId: string | undefined,
  itemRefs: ItemRefs | undefined,
  playbackRef?: string | undefined
) => {
  return itemId === resourceId
    ? { ...itemRefs, playback: playbackRef }
    : undefined;
};

export const sendAnalyticsItemSelectEvent = (
  action: Action | undefined,
  context: ClientContext | undefined
) => {
  const isSourceItemTemplate = !!context?.item && !!context?.container;

  // only trigger item select event when the source is an item template.
  if (action && isSourceItemTemplate) {
    document.dispatchEvent(
      new AnalyticsActionItemSelectEvent({
        [AnalyticsEventParam.ContentId]: context.item?.id,
        [AnalyticsEventParam.ScreenTitle]: getScreenTitle(),
        [AnalyticsEventParam.ScreenType]: getScreenType(context?.screenType),
        [AnalyticsEventParam.Title]: context.item?.title,
        [AnalyticsEventParam.ItemIndex]: context.item?.index,
        [AnalyticsEventParam.ContainerId]:
          context.container?._meta?.containerId,
        [AnalyticsEventParam.ContainerTitle]: context.container?.title,
        [AnalyticsEventParam.ContainerIndex]:
          context.container?._meta?.containerIndex,
        [AnalyticsEventParam.TargetAction]: action.kind,
        [AnalyticsEventParam.Label]: falsyToNull(action?.parameters?.label),
      })
    );
  }
};

interface PerformFavoriteActionParams {
  /** Resolves to whether the list actually changed. */
  action: () => Promise<boolean>;
  mutate: any;
  updatePageContext?: Function;
  showSnackbar?: {
    message: string;
    actionText?: string;
    onAction?: () => void;
  };
}

export const performFavoriteAction = async ({
  action,
  mutate,
  updatePageContext,
  showSnackbar,
}: PerformFavoriteActionParams) => {
  try {
    const didChange = await action();

    if (!didChange) {
      return;
    }

    // The confirmation belongs to the mutation, not to the revalidation that
    // follows it. This used to sit behind `await mutate(...)`, so the snackbar
    // waited on an extra round trip after an already two-request mutation -
    // by the time it appeared the interaction read as broken rather than
    // confirmed.
    if (showSnackbar) {
      snackbarActions.showSnackbar(showSnackbar);
    }

    updatePageContext?.({ lastUpdated: new Date().toISOString() });
    // Deliberately not awaited: this refreshes the favourites feed for
    // whatever else is on screen, and nothing below depends on its result.
    mutate(FAVORITES_CACHE_KEY).catch(ignoreError);
  } catch (error) {
    ignoreError(error);
  }
};

export const useActions = ({
  sharingModalRef,
  showMoreModalRef,
  updatePageContext,
}: {
  sharingModalRef?: RefObject<SharingModalApi>;
  showMoreModalRef?: RefObject<ShowMoreModalApi>;
  updatePageContext?: Function;
} = {}) => {
  const { go, goBack } = useNavigation('useActions');
  const { mutate } = useSWRConfig();

  const paywallAction = (params: ShowPaywallParams) => {
    paywallActions
      .showPaywall(params)
      .then(async (res) => {
        console.debug('Action Hook - Paywall result', res);

        if ([PaywallResult.Payed, PaywallResult.HasAccess].includes(res)) {
          // refresh profile, and update auth state after payment
          await authActions.refreshProfile();
        }
      })
      .catch((err) => {
        if (err === PaywallResult.Aborted) {
          return;
        }
        console.error('Action Hook - Paywall error', err);
      });
  };

  const cancelAction = async () => {
    paywallStore.cancel.isLoading = true;

    try {
      await paywallStore.paywallApi.cancelUserProduct!(
        paywallStore.cancel.selectedProductId,
        ProductType.Subscription
      );

      await authActions.refreshProfile();
      paywallActions.refreshProducts();

      document.dispatchEvent(
        new AnalyticsActionCancelEvent({
          [AnalyticsEventParam.SubscriptionId]:
            paywallStore.cancel.selectedProductId,
        })
      );

      if (paywallStore.cancel.isSaveCampaignEnabled) {
        document.dispatchEvent(
          new AnalyticsActionSaveCampaignDeclineEvent({
            [AnalyticsEventParam.SubscriptionId]:
              paywallStore.cancel.selectedProductId,
          })
        );
      }

      actions[ActionKind.NavigateBack]?.();
      paywallStore.cancel.isDone = true;
    } catch (error) {
      console.error('Cancel error', error);
      paywallActions.setCancelError(
        new OneError(Errors.WEB_INTEGRATION_GENERAL_UNKNOWN, {
          extras: {
            method: paywallStore.cancel.isSaveCampaignEnabled
              ? 'cancelProduct (standard)'
              : 'cancelProduct (save campaign)',
          },
          cause: error,
        })
      );
    } finally {
      paywallStore.cancel.isLoading = false;
    }
  };

  const playAction =
    (): ActionsDictionary[ActionKind.Play] =>
    async (
      resourceId,
      mediaType,
      item?: DynUiItem,
      context?: ClientContext,
      action?
    ) => {
      upNextActions.setStartFromBeginning(false);
      const resourceType = mediaTypeToResourceType[mediaType] || mediaType;
      console.debug(
        `Action Hook - Action: ${ActionKind.Play}`,
        resourceId,
        resourceType
      );
      // DynUi can pass back any resourceType/resourceId they want, we care about the current item in context specifically here
      const itemSpecificResourceType =
        item?.type?.toLocaleLowerCase?.() as ResourceType;
      const itemRefs =
        itemSpecificResourceType === ResourceType.Collection
          ? item?.upNext?.refs
          : item?.refs;
      const videoRef = item?.video?.playback;

      sendAnalyticsItemSelectEvent(action, context);

      go(
        `/playback/${resourceType}/${resourceId}`,
        getItemRefs(item?.id, resourceId, itemRefs, videoRef)
      );
    };

  const shareAction =
    (): ActionsDictionary[ActionKind.Share] => (paramUrl, item: DynUiItem) => {
      console.debug(
        `Action Hook - Action: ${ActionKind.Share}`,
        paramUrl,
        item
      );
      sharingModalRef?.current?.open(item as ClientItem);
    };

  const purchaseAction =
    (): ActionsDictionary[ActionKind.Purchase] =>
    async (
      resourceId,
      mediaType,
      contextItem?: DynUiItem,
      context?: ClientContext,
      action?
    ) => {
      const resourceType =
        (mediaType && mediaTypeToResourceType[mediaType]) || mediaType;
      console.debug(`Action Hook - Action: ${ActionKind.Purchase}`, {
        resourceId,
        resourceType,
        contextItem,
      });

      sendAnalyticsItemSelectEvent(action, context);

      paywallActions.setPurchaseEntitlements(
        contextItem?.video?.entitlementTags
      );

      paywallAction({
        fetchWithEntitlementTags: contextItem?.video?.entitlementTags,
        filterOutMatchingEntitlements:
          authStore.entitlements.grantedEntitlementTags,
        filterOutSubscriptionGroupIds: undefined,
        subscriptionGroups: undefined,
      });
    };

  const openPrivacyAction =
    (): ActionsDictionary[ActionKind.OpenPrivacyLawSettings] => () => {
      console.debug(
        `Action Hook - Action: ${ActionKind.OpenPrivacyLawSettings}`
      );
      privacyActions.showCpUi();
    };

  const actions: ActionsDictionary = {
    [ActionKind.NavigateExternal]: (
      link,
      _contextItem,
      _params,
      context?: DynUiContext,
      action?
    ) => {
      console.debug(
        `Action Hook - Action: ${ActionKind.NavigateExternal} `,
        link
      );

      sendAnalyticsItemSelectEvent(action, context);

      window.open(link, '_blank');
    },

    [ActionKind.NavigateInternal]: (
      view,
      _contextItem,
      _params,
      context?: DynUiContext,
      action?
    ) => {
      console.debug(
        `Action Hook - Action: ${ActionKind.NavigateInternal}`,
        view
      );

      sendAnalyticsItemSelectEvent(action, context);

      switch (view) {
        case InternalPageId.Iap:
          paywallAction({
            fetchWithEntitlementTags: undefined,
            filterOutMatchingEntitlements:
              authStore.entitlements.grantedEntitlementTags,
            filterOutSubscriptionGroupIds: undefined,
            subscriptionGroups: undefined,
          });
          break;
        case InternalPageId.Login:
          authActions.showLogin();
          break;
        case InternalPageId.Register:
        case InternalPageId.WatchHistory:
        case InternalPageId.Search:
        case InternalPageId.Favorites:
        case InternalPageId.SubscriptionCancel:
        case InternalPageId.ProgramList:
        case InternalPageId.Settings:
        case InternalPageId.More:
        case InternalPageId.ForgotPassword:
        case InternalPageId.TermsAndConditions:
          if (destinationRoutes[view]) {
            go(destinationRoutes[view]);
          }
          break;
        default:
          // treat unknown `view` values as relative paths
          go('/' + view);
      }
    },

    [ActionKind.NavigatePage]: (
      params,
      item?: DynUiItem,
      context?: DynUiContext,
      action?
    ) => {
      console.debug(
        `Action Hook - Action: ${ActionKind.NavigatePage}`,
        params,
        context
      );

      const { pageAlias, pageId } = params;

      const pathname = generatePagePathname({
        pageId: falsyToNull(pageId) ?? falsyToNull(pageAlias) ?? '',
        resourceId: params.context?.objectId,
        resourceType: params.context?.objectType as ResourceType,
        itemName: item?.title,
        itemId: item?.id,
      });

      const itemRefs = item?.refs;
      const videoRef = item?.video?.playback;

      sendAnalyticsItemSelectEvent(action, context);

      go(
        pathname,
        getItemRefs(item?.id, params.context?.objectId, itemRefs, videoRef)
      );
    },

    [ActionKind.NavigateBack]: () => {
      console.debug(`Action Hook - Action: ${ActionKind.NavigateBack}`);

      goBack();
    },

    [ActionKind.Play]: playAction(),

    [ActionKind.Share]: shareAction(),

    [ActionKind.Purchase]: purchaseAction(),

    [ActionKind.RemoveFromWatchHistory]: async (itemId, mediaType) => {
      console.debug(
        `Action Hook - Action: ${ActionKind.RemoveFromWatchHistory}`
      );

      try {
        await historyActions.removeFromHistory(itemId, mediaType);
        updatePageContext?.({ lastUpdated: new Date().toISOString() });
        await mutate(WATCH_HISTORY_CACHE_KEY);

        snackbarActions.showSnackbar({
          message: 'edit.removedFromList',
        });
      } catch (error) {
        ignoreError(error);
      }
    },

    [ActionKind.RemoveFromFavorites]: async (itemId, mediaType) => {
      console.debug(`Action Hook - Action: ${ActionKind.RemoveFromFavorites}`);

      await performFavoriteAction({
        action: () => favoritesActions.removeFavorite(itemId, mediaType),
        mutate,
        updatePageContext,
        showSnackbar: {
          message: 'edit.removedFromList',
          actionText: 'button.undo',
          onAction: () => {
            performFavoriteAction({
              action: () => favoritesActions.addFavorite(itemId, mediaType),
              mutate,
              updatePageContext,
            });
          },
        },
      });
    },

    [ActionKind.AddToFavorites]: async (itemId, mediaType) => {
      console.debug(`Action Hook - Action: ${ActionKind.AddToFavorites}`);

      await performFavoriteAction({
        action: () => favoritesActions.addFavorite(itemId, mediaType),
        mutate,
        updatePageContext,
        showSnackbar: {
          message: 'edit.addedToList',
          actionText: 'button.undo',
          onAction: () => {
            performFavoriteAction({
              action: () => favoritesActions.removeFavorite(itemId, mediaType),
              mutate,
              updatePageContext,
            });
          },
        },
      });
    },

    [ActionKind.OpenPrivacyLawSettings]: openPrivacyAction(),
    [ActionKind.Register]: (
      resourceId,
      mediaType,
      contextItem?: DynUiItem,
      context?: DynUiContext,
      action?
    ) => {
      const resourceType =
        (mediaType && mediaTypeToResourceType[mediaType]) || mediaType;
      console.debug(`Action Hook - Action: ${ActionKind.Register}`, {
        resourceId,
        resourceType,
        contextItem,
      });

      sendAnalyticsItemSelectEvent(action, context);

      paywallActions.setPurchaseEntitlements(
        contextItem?.video?.entitlementTags
      );

      go(Path.Registration);
    },

    [ActionKind.Logout]: () => {
      console.debug(`Action Hook - Action: ${ActionKind.Logout}`);
      userService
        .logout()
        .then(() => authActions.logout())
        .catch(ignoreError);
    },

    [ActionKind.Login]: (
      resourceId,
      mediaType,
      contextItem?: DynUiItem,
      context?: DynUiContext,
      action?
    ) => {
      const resourceType =
        (mediaType && mediaTypeToResourceType[mediaType]) || mediaType;
      console.debug(`Action Hook - Action: ${ActionKind.Login}`, {
        resourceId,
        resourceType,
        contextItem,
      });

      sendAnalyticsItemSelectEvent(action, context);

      paywallActions.setPurchaseEntitlements(
        contextItem?.video?.entitlementTags
      );
      authActions.showLogin();
    },

    [ActionKind.SetValue]: (path, value, context: DynUiContext = {}) => {
      // get the value and if it looks like a boolean, turn it into a boolean
      const boundValue = applyDatabinding({ value, context });
      const typedValue = ['true', 'false'].includes(boundValue)
        ? boundValue === 'true'
        : boundValue;

      // split the path to the names of the parent objects and treat the last one as the field name
      const names = path.split('.');
      const field = names.pop();

      if (!field) {
        return;
      }

      // starting from the context, go along the names and find/create all the parent objects
      let parent = context;

      for (const name of names) {
        parent[name] ??= {};
        parent = parent[name];
      }

      // set the new value and rerender DynUi
      parent[field] = typedValue;
      updatePageContext?.();
    },

    [ActionKind.CancelProduct]: async () => {
      console.debug(`Action Hook - Action: ${ActionKind.CancelProduct}`);

      await cancelAction();
    },

    [ActionKind.SaveCampaignDecline]: async () => {
      console.debug(`Action Hook - Action: ${ActionKind.SaveCampaignDecline}`);

      await cancelAction();
    },

    [ActionKind.SaveCampaignAccept]: async () => {
      console.debug(`Action Hook - Action: ${ActionKind.SaveCampaignAccept}`);

      paywallStore.cancel.isLoading = true;

      try {
        await paywallStore.paywallApi.applySaveCampaignOffer!(
          paywallStore.cancel.userSubscriptionId
        );

        paywallActions.refreshProducts();

        document.dispatchEvent(
          new AnalyticsActionSaveCampaignAcceptEvent({
            [AnalyticsEventParam.OfferName]:
              paywallStore.cancel.saveCampaignOfferName,
            [AnalyticsEventParam.SubscriptionId]:
              paywallStore.cancel.selectedProductId,
          })
        );

        actions[ActionKind.NavigateBack]?.();
        paywallStore.cancel.isDone = true;
      } catch (error) {
        ignoreError(error);
        paywallActions.setCancelError(
          new OneError(Errors.WEB_INTEGRATION_GENERAL_UNKNOWN, {
            extras: { method: 'SaveCampaignAccept' },
          })
        );
      } finally {
        paywallStore.cancel.isLoading = false;
      }
    },

    [ActionKind.ShowMore]: (text, context = {}) => {
      showMoreModalRef?.current?.open({
        title: applyDatabinding({ value: '{{item.title}}', context }),
        text,
      });
    },
  };

  const handleAction = (action?: Action, context: DynUiContext = {}) => {
    if (!action) {
      return;
    }
    eventHandlers(context, actions, action)?.onClick();
  };

  return {
    actions,
    handleAction,
  };
};
